// RiveContext.tsx
import React from "react"
import { Platform } from "react-native"

import { RiveRef } from "rive-react-native"
import { TSentence, TViseme, TWord } from "types/speechMarks"
import { TConversation } from "types/transcript"

import { avatarUserForConversation, UnknownAvatar } from "src/components/ChatUser"
import useSpeechMarkPlayer from "src/hooks/useSpeechMarkPlayer"
import { useSpeechMarkPlayerWeb } from "src/hooks/useSpeechMarkPlayerWeb"
import { useCurrentMsk } from "src/queries/useConfig"
import { useCurrentConversation } from "src/queries/useConversation"
import type { Rive } from "@rive-app/canvas"
import { useAuth } from "./AuthContext"

const UNDEFINED_AVATAR_ID = -1

// Union type for both platforms
type RiveInstance = RiveRef | Rive
type RiveOwner = "primary" | "video"

// Export avatar image map for use in other components
export const avatarImageMap: Record<string, any> = {
  "00": require("../../assets/images/avatar_images/00.png"),
  "01": require("../../assets/images/avatar_images/01.png"),
  "02": require("../../assets/images/avatar_images/02.png"),
  "03": require("../../assets/images/avatar_images/03.png"),
  "04": require("../../assets/images/avatar_images/04.png"),
  "05": require("../../assets/images/avatar_images/05.png"),
  "06": require("../../assets/images/avatar_images/06.png"),
  "07": require("../../assets/images/avatar_images/07.png"),
  "08": require("../../assets/images/avatar_images/08.png"),
  "09": require("../../assets/images/avatar_images/09.png"),
  "10": require("../../assets/images/avatar_images/10.png"),
  "11": require("../../assets/images/avatar_images/11.png"),
  "12": require("../../assets/images/avatar_images/12.png"),
  "13": require("../../assets/images/avatar_images/13.png"),
  "14": require("../../assets/images/avatar_images/14.png"),
  "15": require("../../assets/images/avatar_images/15.png"),
  "16": require("../../assets/images/avatar_images/16.png"),
  "17": require("../../assets/images/avatar_images/17.png"),
  "18": require("../../assets/images/avatar_images/18.png"),
  "19": require("../../assets/images/avatar_images/19.png"),
  "20": require("../../assets/images/avatar_images/20.png"),
  "21": require("../../assets/images/avatar_images/21.png"),
  "22": require("../../assets/images/avatar_images/22.png"),
  "23": require("../../assets/images/avatar_images/23.png"),
  "24": require("../../assets/images/avatar_images/24.png"),
  "25": require("../../assets/images/avatar_images/25.png"),
  "26": require("../../assets/images/avatar_images/26.png"),
  "27": require("../../assets/images/avatar_images/27.png"),
  "28": require("../../assets/images/avatar_images/28.png"),
  "29": require("../../assets/images/avatar_images/29.png"),
  "30": require("../../assets/images/avatar_images/30.png"),
  "31": require("../../assets/images/avatar_images/31.png"),
  "32": require("../../assets/images/avatar_images/32.png"),
  "33": require("../../assets/images/avatar_images/33.png"),
  "34": require("../../assets/images/avatar_images/34.png"),
  "35": require("../../assets/images/avatar_images/35.png"),
  "36": require("../../assets/images/avatar_images/36.png"),
  "37": require("../../assets/images/avatar_images/37.png"),
  "38": require("../../assets/images/avatar_images/38.png"),
  "39": require("../../assets/images/avatar_images/39.png"),
  "40": require("../../assets/images/avatar_images/40.png"),
  "41": require("../../assets/images/avatar_images/41.png"),
  "42": require("../../assets/images/avatar_images/42.png"),
  "43": require("../../assets/images/avatar_images/43.png"),
}

export interface CaptionText {
  line1: string
  line2: string
}

export interface RiveContextType {
  riveMskAvatarId?: number
  /** The avatar ID for the current conversation (MSK or ESK), computed synchronously
   *  from `avatarUserForConversation`. Always reflects the latest data without waiting
   *  for the async sync effect to propagate `riveMskAvatarId`. Use this (when !== -1)
   *  as the authoritative source for `riveResourceName` to avoid a one-render flash of
   *  the wrong avatar on screen transitions (e.g. after MSK selection). */
  resolvedAvatarId: number
  isMsk: boolean
  mskImageSource?: any
  eskImageSource?: any
  avatarBackgroundColor: string
  mskName: string
  avatarName: string
  /**
   * Animate the avatar's mouth from speech marks.
   * @param offsetMs When resuming partway through a clip (e.g. Rive became ready
   *   after audio already started), the elapsed playback time in ms. Visemes before
   *   this point are snapped (the most recent one is applied immediately) and only
   *   future visemes are scheduled, keeping lip sync aligned with the audio.
   */
  handleAnimateRive?: (
    speechMarkData: string | Array<TSentence | TWord | TViseme>,
    offsetMs?: number,
  ) => void
  stopRiveAnimation?: () => void
  isAnimating?: boolean
  registerAnimationFunctions?: (owner: RiveOwner, riveInstance: RiveInstance | null) => void
  toggleIsMsk?: () => void
  isRiveReady?: boolean
  captionText: CaptionText
  isCaptionActive: boolean
  /**
   * Override the resolved avatar ID for unauthenticated screens (e.g. unauth intro message).
   * Pass the string avatar_id from the intro_message response.
   * Set to null to restore the conversation-derived avatar.
   */
  setUnauthAvatarId: (id: string | null) => void
  /**
   * Override the displayed avatar name for unauthenticated screens (e.g. psk_name from intro_message).
   * Set to null to clear (restores empty string until auth sync re-populates it).
   */
  setUnauthAvatarName: (name: string | null) => void
}

const RiveContext = React.createContext<RiveContextType | null>(null)

export const RiveProvider: React.FC<React.PropsWithChildren> = ({ children }) => {
  const { data: currentConversation } = useCurrentConversation()
  const { isAuthenticated } = useAuth()
  const currentMsk = useCurrentMsk()

  const [riveMskAvatarId, setRiveMskAvatarId] = React.useState<number>(-1)
  const [riveEskAvatarId, setRiveEskAvatarId] = React.useState<number>(-1)
  const [mskName, setMskName] = React.useState<string>("")
  const [avatarName, setAvatarName] = React.useState<string>("")
  const [isMsk, setIsMsk] = React.useState<boolean>(false)
  const [isDemoMode, setIsDemoMode] = React.useState<boolean>(false)
  const [unauthAvatarId, setUnauthAvatarId] = React.useState<string | null>(null)
  const [unauthAvatarName, setUnauthAvatarNameState] = React.useState<string | null>(null)

  const setUnauthAvatarName = React.useCallback((name: string | null) => {
    setUnauthAvatarNameState(name)
  }, [])

  const [lastConversation, setLastConversation] = React.useState<TConversation | undefined>(
    currentConversation || undefined,
  )

  // Platform-specific refs
  const activeRiveRefNative = React.useRef<RiveRef | null>(null)
  const activeRiveRefWeb = React.useRef<Rive | null>(null)
  const [activeNativeRiveState, setActiveNativeRiveState] = React.useState<RiveRef | null>(null)
  const [, setWebRiveVersion] = React.useState(0)
  const [isRiveReady, setIsRiveReady] = React.useState<boolean>(false)
  const activeOwnerRef = React.useRef<RiveOwner | null>(null)
  const primaryRiveRef = React.useRef<RiveInstance | null>(null)

  // Platform-specific speech players
  const nativeSpeechPlayer = useSpeechMarkPlayer(activeNativeRiveState)
  const webSpeechPlayer = useSpeechMarkPlayerWeb(activeRiveRefWeb, "State Machine 1")

  // Select the appropriate speech player based on platform
  const { animateSpeech, stop, isAnimating, captionText, isCaptionActive } =
    Platform.OS === "web" ? webSpeechPlayer : nativeSpeechPlayer

  // register rive functions
  const registerAnimationFunctions = React.useCallback(
    (owner: RiveOwner, riveInstance: RiveInstance | null) => {
      const activateRiveInstance = (targetOwner: RiveOwner, instance: RiveInstance) => {
        activeOwnerRef.current = targetOwner
        if (Platform.OS === "web") {
          const webInstance = instance as Rive
          activeRiveRefWeb.current = webInstance
          activeRiveRefNative.current = null
          setActiveNativeRiveState(null)
          setWebRiveVersion((version) => version + 1)
        } else {
          const nativeInstance = instance as RiveRef
          activeRiveRefNative.current = nativeInstance
          activeRiveRefWeb.current = null
          setActiveNativeRiveState(nativeInstance)
          setWebRiveVersion((version) => version + 1)
        }
        setIsRiveReady(true)
      }

      if (owner === "primary") {
        if (riveInstance) {
          primaryRiveRef.current = riveInstance
        } else {
          primaryRiveRef.current = null
        }
      }

      if (!riveInstance) {
        if (activeOwnerRef.current === owner) {
          activeOwnerRef.current = null
          activeRiveRefNative.current = null
          activeRiveRefWeb.current = null
          setActiveNativeRiveState(null)
          setWebRiveVersion((version) => version + 1)
          setIsRiveReady(false)

          if (owner === "video" && primaryRiveRef.current) {
            activateRiveInstance("primary", primaryRiveRef.current)
          }
        }
        return
      }

      if (owner === "primary" && activeOwnerRef.current === "video") {
        return
      }

      activateRiveInstance(owner, riveInstance)
    },
    [],
  )

  // toggle function for demo purposes
  const toggleIsMsk = React.useCallback(() => {
    setIsDemoMode(true)
    setIsMsk((prev) => !prev)
    console.log("[RIVE_CONTEXT] Toggling isMsk in demo mode")
  }, [])

  // Use the exported avatar image map
  const imageMap = avatarImageMap

  React.useEffect(() => {
    if (currentConversation && currentConversation !== lastConversation) {
      setLastConversation(currentConversation)
    }
  }, [currentConversation, lastConversation])

  const currentMskAvatarId = React.useMemo(() => {
    if (!currentMsk) return UNDEFINED_AVATAR_ID

    const parsedId = parseInt(currentMsk.avatar_id, 10)
    return Number.isNaN(parsedId) ? UNDEFINED_AVATAR_ID : parsedId
  }, [currentMsk])

  const mskImageSource = React.useMemo(() => {
    const avatarId =
      currentMskAvatarId !== UNDEFINED_AVATAR_ID ? currentMskAvatarId : riveMskAvatarId
    return imageMap[avatarId.toString().padStart(2, "0")]
  }, [currentMskAvatarId, riveMskAvatarId, imageMap])

  const eskImageSource = React.useMemo(() => {
    return imageMap[riveEskAvatarId.toString().padStart(2, "0")]
  }, [riveEskAvatarId, imageMap])

  // Use only current conversation for avatar so we never show the previous sidekick after
  // closing (e.g. selecting a new MSK). lastConversation is kept for history/cache only.
  const conversationForAvatar = currentConversation ?? undefined

  const {
    avatarId: currentAvatarId,
    name: currentAvatarName,
    colors: currentAvatarColors,
    isMsk: currentAvatarIsMsk,
  } = React.useMemo(
    () => avatarUserForConversation(conversationForAvatar, currentMsk),
    [conversationForAvatar, currentMsk],
  )

  const avatarBackgroundColor = currentAvatarColors.background || "#ffffff"

  React.useEffect(() => {
    if (!isAuthenticated || !currentMsk) return

    if (currentAvatarName === UnknownAvatar.name) return
    setMskName(currentMsk.name)

    if (
      currentAvatarId !== UNDEFINED_AVATAR_ID &&
      (currentAvatarId !== riveMskAvatarId ||
        currentAvatarIsMsk !== isMsk ||
        currentAvatarName !== avatarName)
    ) {
      // Only set isMsk if not in demo mode
      if (!isDemoMode) {
        setIsMsk(currentAvatarIsMsk)
        if (!currentAvatarIsMsk) {
          setRiveEskAvatarId(currentAvatarId)
        }
      }

      setRiveMskAvatarId(currentAvatarId)
      setAvatarName(currentAvatarName)
      // Reset ready state when avatar changes
      setIsRiveReady(false)
    }
  }, [
    currentMsk,
    currentAvatarId,
    currentMskAvatarId,
    riveMskAvatarId,
    isAuthenticated,
    currentAvatarName,
    avatarName,
    isDemoMode,
    isMsk,
    currentAvatarIsMsk,
    conversationForAvatar?.id,
  ])

  // Prefer the unauth override avatar ID when set (e.g. from intro_message response).
  const effectiveResolvedAvatarId = React.useMemo(() => {
    if (unauthAvatarId !== null) {
      const parsed = parseInt(unauthAvatarId, 10)
      return isNaN(parsed) ? currentAvatarId : parsed
    }
    return currentAvatarId
  }, [unauthAvatarId, currentAvatarId])

  const value: RiveContextType = React.useMemo(
    () => ({
      riveMskAvatarId,
      resolvedAvatarId: effectiveResolvedAvatarId,
      isMsk,
      mskImageSource,
      eskImageSource,
      avatarBackgroundColor,
      mskName,
      avatarName: unauthAvatarName ?? avatarName,
      handleAnimateRive: animateSpeech,
      stopRiveAnimation: stop,
      isAnimating,
      registerAnimationFunctions,
      toggleIsMsk,
      isRiveReady,
      captionText: captionText ?? { line1: "", line2: "" },
      isCaptionActive: isCaptionActive ?? false,
      setUnauthAvatarId,
      setUnauthAvatarName,
    }),
    [
      riveMskAvatarId,
      effectiveResolvedAvatarId,
      isMsk,
      mskImageSource,
      eskImageSource,
      avatarBackgroundColor,
      mskName,
      unauthAvatarName,
      avatarName,
      animateSpeech,
      stop,
      isAnimating,
      registerAnimationFunctions,
      toggleIsMsk,
      isRiveReady,
      captionText,
      isCaptionActive,
      setUnauthAvatarId,
      setUnauthAvatarName,
    ],
  )

  return <RiveContext.Provider value={value}>{children}</RiveContext.Provider>
}

export const useRiveContext = () => {
  const context = React.useContext(RiveContext)
  if (!context) {
    throw new Error("useRiveContext must be used within an RiveProvider")
  }
  return context
}
